/**
 * Backlog preview + full backlog table used on Screen 3 and Screen 8.
 * `variant="preview"` shows top-5 with only demote; `variant="full"` adds
 * promote + remove actions and a header.
 */
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty';
import { Spinner } from '@/components/spinner';
import { ErrorBanner } from '@/components/error-banner';
import { summarizeApiError } from '@/lib/api';
import {
  useDeleteBacklogCluster,
  useDemoteBacklogCluster,
  usePromoteBacklogCluster,
} from '@/features/auto-campaign/mutations';
import type { AutoCampaignBacklogItem } from '@/features/auto-campaign/types';

export interface BacklogTableProps {
  projectId: string;
  items: AutoCampaignBacklogItem[] | undefined;
  isLoading: boolean;
  error: unknown;
  variant?: 'preview' | 'full';
  previewLimit?: number;
  /** Called by the preview variant when the user clicks "Full backlog". */
  onOpenFullBacklog?: () => void;
}

export function BacklogTable({
  projectId,
  items,
  isLoading,
  error,
  variant = 'preview',
  previewLimit = 5,
  onOpenFullBacklog,
}: BacklogTableProps): JSX.Element {
  const promote = usePromoteBacklogCluster(projectId);
  const demote = useDemoteBacklogCluster(projectId);
  const remove = useDeleteBacklogCluster(projectId);

  const isFull = variant === 'full';
  const shown = isFull ? items ?? [] : (items ?? []).slice(0, previewLimit);

  async function handleAction(
    label: string,
    action: () => Promise<unknown>,
  ): Promise<void> {
    try {
      await action();
      toast.success(label);
    } catch (e) {
      const s = summarizeApiError(e, { fallbackTitle: `${label} failed` });
      toast.error(s.title, { description: s.description });
    }
  }

  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle>
            Upcoming brief topics
            {items ? (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                ({items.length}
                {items.length === 50 && !isFull ? '+' : ''} clusters)
              </span>
            ) : null}
          </SectionTitle>
          <SectionDescription>
            Keyword clusters queued for future runs — the auto-pilot writes
            briefs for the highest-priority ones each run.
          </SectionDescription>
        </div>
      </SectionHeader>

      {error ? <ErrorBanner error={error} /> : null}

      {isLoading ? (
        <div className="flex items-center justify-center p-6">
          <Spinner />
        </div>
      ) : shown.length === 0 ? (
        <EmptyState
          title="Backlog is empty"
          description="Uncovered keyword clusters will queue here after each keyword refresh."
          variant="inline"
        />
      ) : (
        <div className="overflow-hidden rounded-md border border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>Pillar keyword</TableHead>
                <TableHead className="w-20 text-right">Score</TableHead>
                <TableHead className="w-56 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((row, idx) => {
                const busy =
                  (promote.isPending && promote.variables === row.id) ||
                  (demote.isPending && demote.variables === row.id) ||
                  (remove.isPending && remove.variables === row.id);
                return (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {idx + 1}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">
                        {row.pillar_keyword}
                      </div>
                      {row.reason_uncovered ? (
                        <div className="text-xs text-muted-foreground">
                          {row.reason_uncovered}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums">
                      {Number(row.priority_score).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {isFull ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() =>
                              handleAction('Promoted', () =>
                                promote.mutateAsync(row.id),
                              )
                            }
                            title="Promote — bump above current max"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Promote</span>
                          </Button>
                        ) : null}
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() =>
                            handleAction('Demoted', () =>
                              demote.mutateAsync(row.id),
                            )
                          }
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Demote</span>
                        </Button>
                        {isFull ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => {
                              if (
                                !window.confirm(
                                  `Remove "${row.pillar_keyword}" from the backlog? This is permanent — it won't be re-queued until the next keyword refresh.`,
                                )
                              )
                                return;
                              void handleAction('Removed', () =>
                                remove.mutateAsync(row.id),
                              );
                            }}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Remove</span>
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {!isFull && (items?.length ?? 0) > previewLimit && onOpenFullBacklog ? (
        <div className="mt-3 flex justify-end">
          <Button variant="outline" size="sm" onClick={onOpenFullBacklog}>
            Full backlog
          </Button>
        </div>
      ) : null}
    </Section>
  );
}
